import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  Trophy,
  Users,
  Target,
  ChevronRight,
  Plus,
  Calendar,
  CheckCircle2,
  Sparkles,
  Flag,
  Coins,
  Bot,
  MessageSquare,
  Wallet,
  Gift
} from "lucide-react";
import { Button } from "./ui/button";
import UnlockFeatureModal, { UNLOCKABLE_FEATURES, COIN_EARNING_METHODS } from "./UnlockFeatureModal";
import UnlockCelebration from "./UnlockCelebration";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Phase display configuration
const PHASES = {
  1: {
    name: "Track",
    description: "Log your rounds to build your handicap",
    icon: Target,
    bgClass: "bg-emerald-500/10",
    borderClass: "border-emerald-500/30",
    textClass: "text-emerald-400",
    badgeBg: "bg-emerald-500/20"
  },
  2: {
    name: "Connect", 
    description: "Find golfers to play with",
    icon: Users,
    bgClass: "bg-blue-500/10",
    borderClass: "border-blue-500/30",
    textClass: "text-blue-400",
    badgeBg: "bg-blue-500/20"
  },
  3: {
    name: "Compete",
    description: "Join tournaments and outings",
    icon: Trophy,
    bgClass: "bg-amber-500/10",
    borderClass: "border-amber-500/30",
    textClass: "text-amber-400",
    badgeBg: "bg-amber-500/20"
  },
  4: {
    name: "Elevate",
    description: "Full access to all features",
    icon: Sparkles,
    bgClass: "bg-purple-500/10",
    borderClass: "border-purple-500/30",
    textClass: "text-purple-400",
    badgeBg: "bg-purple-500/20"
  }
};

// Feature categories for the grid
const FEATURE_CATEGORIES = [
  {
    title: "Connect",
    phase: 2,
    features: ["find_golfers", "open_tee_times", "golf_buddy", "social_feed"]
  },
  {
    title: "Compete", 
    phase: 3,
    features: ["match_play", "skins_games", "stroke_play", "golf_outings"]
  },
  {
    title: "Elevate",
    phase: 4,
    features: ["ai_assistant", "advanced_stats"]
  }
];

const NewUserDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [milestones, setMilestones] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentScores, setRecentScores] = useState([]);
  const [userCoins, setUserCoins] = useState(0);
  const [unlockedFeatures, setUnlockedFeatures] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationFeature, setCelebrationFeature] = useState(null);
  
  // Animated Earn Coins card state
  const [coinCardAnimationPhase, setCoinCardAnimationPhase] = useState('hidden'); // hidden, flying, spinning, settled
  const coinCardRef = useRef(null);

  const fetchMilestones = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/milestones/status`);
      setMilestones(response.data);
    } catch (error) {
      console.error("Error fetching milestones:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecentScores = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/scores?limit=5`);
      setRecentScores(response.data || []);
    } catch (error) {
      console.error("Error fetching scores:", error);
    }
  }, []);

  const fetchUserCoins = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/golf-coins/balance`);
      setUserCoins(response.data?.balance || 0);
    } catch (error) {
      console.error("Error fetching coins:", error);
      setUserCoins(0);
    }
  }, []);

  const fetchUnlockedFeatures = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/features/unlocked`);
      setUnlockedFeatures(response.data?.unlocked_features || []);
    } catch (error) {
      // API might not exist yet, use empty array
      setUnlockedFeatures([]);
    }
  }, []);

  useEffect(() => {
    fetchMilestones();
    fetchRecentScores();
    fetchUserCoins();
    fetchUnlockedFeatures();
  }, [fetchMilestones, fetchRecentScores, fetchUserCoins, fetchUnlockedFeatures]);

  // Animated Earn Coins card entrance sequence
  useEffect(() => {
    if (!loading) {
      // Start animation sequence after page loads
      const timer1 = setTimeout(() => setCoinCardAnimationPhase('flying'), 300);
      const timer2 = setTimeout(() => setCoinCardAnimationPhase('spinning'), 800);
      const timer3 = setTimeout(() => setCoinCardAnimationPhase('settled'), 1800);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [loading]);

  // Check if a feature is unlocked (by coins, rounds, or phase)
  const isFeatureUnlocked = (featureId) => {
    // Check if unlocked with coins
    if (unlockedFeatures.includes(featureId)) return true;
    
    // Check phase-based unlocks from milestones
    const feature = UNLOCKABLE_FEATURES[featureId];
    if (!feature || !milestones) return false;

    const roundsCompleted = milestones.rounds_completed || 0;
    return roundsCompleted >= feature.freeUnlock.count;
  };

  const handleFeatureClick = (featureId) => {
    if (isFeatureUnlocked(featureId)) {
      // Navigate to the feature
      const routes = {
        find_golfers: '/find-golfers',
        open_tee_times: '/find-golfers?tab=tee-times',
        golf_buddy: '/find-golfers?tab=buddies',
        social_feed: '/chat',
        match_play: '/games?tab=match-play',
        skins_games: '/games?tab=skins',
        stroke_play: '/games?tab=stroke-play',
        golf_outings: '/outings',
        ai_assistant: '/assistant',
        advanced_stats: '/stats'
      };
      navigate(routes[featureId] || '/home');
    } else {
      // Show unlock modal
      setSelectedFeature(featureId);
      setShowUnlockModal(true);
    }
  };

  const handleUnlockWithCoins = async (featureId, cost) => {
    try {
      await axios.post(`${API}/features/unlock`, { feature_id: featureId });
      setShowUnlockModal(false);
      setUnlockedFeatures([...unlockedFeatures, featureId]);
      setUserCoins(prev => prev - cost);
      
      // Show celebration animation
      setCelebrationFeature(UNLOCKABLE_FEATURES[featureId]?.name || featureId);
      setShowCelebration(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unlock feature");
    }
  };

  const handleCelebrationComplete = () => {
    setShowCelebration(false);
    setCelebrationFeature(null);
  };

  const handleBuyCoins = () => {
    setShowUnlockModal(false);
    navigate('/golf-coins?tab=buy');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const phase = milestones?.phase || 1;
  const phaseConfig = PHASES[phase];
  const PhaseIcon = phaseConfig.icon;
  const roundsCompleted = milestones?.rounds_completed || 0;
  const roundsToHandicap = milestones?.rounds_to_handicap ?? 3;

  return (
    <div className="ps-theme-page min-h-screen pb-24" data-testid="new-user-home-page">
      {/* Unlock Celebration Animation */}
      <UnlockCelebration
        show={showCelebration}
        featureName={celebrationFeature}
        onComplete={handleCelebrationComplete}
      />

      {/* Unlock Feature Modal */}
      <UnlockFeatureModal
        feature={selectedFeature}
        isOpen={showUnlockModal}
        onClose={() => setShowUnlockModal(false)}
        userCoins={userCoins}
        onBuyCoins={handleBuyCoins}
        onUnlockWithCoins={handleUnlockWithCoins}
      />

      {/* Header with Coin Balance */}
      <div className="ps-page-header pt-4 pb-6 px-4" data-testid="new-user-home-header">
        <div className="max-w-lg mx-auto">
          {/* Top Bar - Phase & Coins */}
          <div className="flex items-center justify-between mb-6">
            {/* Phase Badge */}
            <div className={`px-3 py-1.5 rounded-full ${phaseConfig.badgeBg} border ${phaseConfig.borderClass}`}>
              <div className="flex items-center gap-2">
                <PhaseIcon className={`w-4 h-4 ${phaseConfig.textClass}`} />
                <span className={`text-sm font-medium ${phaseConfig.textClass}`}>
                  {phaseConfig.name}
                </span>
              </div>
            </div>

            {/* Coin Balance */}
            <button 
              onClick={() => navigate('/wallet?tab=coins')}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 transition-colors"
            >
              <Coins className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 font-bold">{userCoins}</span>
            </button>
          </div>

          {/* Handicap Display or Progress */}
          {milestones?.handicap_unlocked && milestones?.handicap != null ? (
            <div className="text-center mb-6">
              <div className="text-6xl font-bold text-white mb-2">
                {typeof milestones.handicap === 'number' ? milestones.handicap.toFixed(1) : '--.-'}
              </div>
              <div className="text-gray-400">Your Handicap Index</div>
              <div className="flex items-center justify-center gap-1 mt-2 text-emerald-400 text-sm">
                <CheckCircle2 className="w-4 h-4" />
                <span>Qualified • {roundsCompleted} rounds</span>
              </div>
            </div>
          ) : (
            <div className="text-center mb-6">
              {/* Large Handicap Placeholder */}
              <div className="relative">
                <div className="text-7xl font-bold text-gray-700 mb-2 tracking-tight">--.-</div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 rounded-full border-2 border-dashed border-emerald-500/30 animate-spin" style={{ animationDuration: '20s' }} />
                </div>
              </div>
              
              {/* Motivational Message */}
              <div className="text-xl text-white font-medium mb-2">
                {roundsToHandicap === 3 ? "Let's get started!" : 
                 roundsToHandicap === 2 ? "Great start! Keep going!" :
                 roundsToHandicap === 1 ? "Almost there!" : "Calculating..."}
              </div>
              <div className="text-gray-400 mb-4">
                {roundsToHandicap > 0 
                  ? `${roundsToHandicap} more round${roundsToHandicap !== 1 ? 's' : ''} until your handicap is calculated`
                  : "Processing your scores..."}
              </div>
              
              {/* Visual Progress Steps */}
              <div className="flex justify-center gap-3 mb-4">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${
                      roundsCompleted >= step 
                        ? 'bg-emerald-500 border-emerald-500' 
                        : roundsCompleted === step - 1
                        ? 'border-emerald-500 border-dashed animate-pulse'
                        : 'border-gray-700 bg-gray-800/50'
                    }`}>
                      {roundsCompleted >= step ? (
                        <CheckCircle2 className="w-6 h-6 text-white" />
                      ) : (
                        <span className={`text-lg font-bold ${roundsCompleted === step - 1 ? 'text-emerald-400' : 'text-gray-500'}`}>
                          {step}
                        </span>
                      )}
                    </div>
                    <span className={`text-xs mt-2 ${roundsCompleted >= step ? 'text-emerald-400' : 'text-gray-500'}`}>
                      Round {step}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Primary CTA - Log Round */}
          <Button
            onClick={() => navigate('/add-score')}
            className="ps-home-cta w-full h-14 text-lg font-bold rounded-2xl shadow-lg transition-transform hover:scale-[1.02] active:scale-[0.98]"
            data-testid="log-round-cta"
          >
            <Plus className="w-5 h-5 mr-2" />
            Log Your Round
          </Button>
          
          {/* Enhanced Info Box */}
          <div className="mt-4 p-4 bg-gradient-to-r from-emerald-900/30 to-emerald-800/10 border border-emerald-500/20 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h4 className="text-white font-semibold mb-1">Quick Start Tips</h4>
                <ul className="text-gray-400 text-sm space-y-1">
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-400">•</span>
                    <span><strong className="text-white">Past rounds count!</strong> Add any round from your history</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-400">•</span>
                    <span><strong className="text-white">AI Scorecard Reader</strong> - Snap a photo to auto-fill scores</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-400">•</span>
                    <span><strong className="text-white">Manual entry</strong> - Enter hole-by-hole or just your total</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Coin Reward */}
            <div className="mt-3 pt-3 border-t border-emerald-500/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Coins className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 font-semibold">+10 coins per round</span>
              </div>
              <div className="text-gray-500 text-sm">
                +100 bonus at 3 rounds!
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ ANIMATED EARN COINS CARD ============ */}
      <div className="px-4 py-2">
        <div className="max-w-lg mx-auto">
          <div 
            ref={coinCardRef}
            className={`
              relative overflow-hidden p-4 rounded-xl 
              bg-gradient-to-br from-amber-500/10 via-amber-600/5 to-yellow-500/10 
              border border-amber-500/30
              transition-all duration-700 ease-out
              ${coinCardAnimationPhase === 'hidden' ? 'opacity-0 translate-y-20 scale-50' : ''}
              ${coinCardAnimationPhase === 'flying' ? 'opacity-100 translate-y-0 scale-110' : ''}
              ${coinCardAnimationPhase === 'spinning' ? 'opacity-100 translate-y-0 scale-105' : ''}
              ${coinCardAnimationPhase === 'settled' ? 'opacity-100 translate-y-0 scale-100' : ''}
            `}
            style={{
              boxShadow: coinCardAnimationPhase === 'spinning' 
                ? '0 0 30px rgba(245, 158, 11, 0.4), 0 0 60px rgba(245, 158, 11, 0.2)' 
                : coinCardAnimationPhase === 'settled'
                ? '0 4px 20px rgba(245, 158, 11, 0.15)'
                : 'none'
            }}
          >
            {/* Spinning Coins Background Animation */}
            <div className={`
              absolute inset-0 pointer-events-none overflow-hidden
              ${coinCardAnimationPhase === 'spinning' ? 'opacity-100' : 'opacity-0'}
              transition-opacity duration-300
            `}>
              {[...Array(8)].map((_, i) => (
                <div 
                  key={i}
                  className="absolute text-2xl animate-bounce"
                  style={{
                    left: `${10 + (i * 12)}%`,
                    top: `${20 + (i % 3) * 25}%`,
                    animationDelay: `${i * 0.1}s`,
                    animationDuration: '0.5s',
                    transform: `rotate(${i * 45}deg)`
                  }}
                >
                  🪙
                </div>
              ))}
            </div>
            
            {/* Header with animated coin icon */}
            <div className="flex items-center gap-2 mb-3 relative z-10">
              <div className={`
                transition-all duration-500
                ${coinCardAnimationPhase === 'spinning' ? 'animate-spin' : ''}
              `}
              style={{ animationDuration: '0.3s' }}
              >
                <Gift className="w-5 h-5 text-amber-400" />
              </div>
              <h3 className="font-bold text-white text-lg tracking-wide">EARN GOLF COINS</h3>
            </div>
            
            {/* Earning Methods List */}
            <div className="space-y-2 relative z-10">
              {COIN_EARNING_METHODS.slice(0, 3).map((method, index) => {
                const MethodIcon = method.icon;
                return (
                  <div 
                    key={index} 
                    className={`
                      flex items-center justify-between py-1.5
                      transition-all duration-500
                      ${coinCardAnimationPhase === 'settled' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}
                    `}
                    style={{ transitionDelay: `${index * 100 + 200}ms` }}
                  >
                    <div className="flex items-center gap-2">
                      <MethodIcon className="w-4 h-4 text-amber-500/70" />
                      <span className="text-gray-300 text-sm">{method.action}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-amber-400 font-bold text-sm">+{method.coins}</span>
                      <span className="text-amber-500">🪙</span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Action Buttons */}
            <div className={`
              mt-4 space-y-2 relative z-10
              transition-all duration-500
              ${coinCardAnimationPhase === 'settled' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
            `}
            style={{ transitionDelay: '500ms' }}
            >
              <Button
                onClick={() => navigate('/golf-coins')}
                variant="outline"
                className="w-full border-amber-500/40 text-amber-400 hover:bg-amber-500/20 hover:border-amber-400 transition-all"
              >
                View All Ways to Earn
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <Button
                onClick={() => navigate('/achievements')}
                variant="outline"
                className="w-full border-purple-500/40 text-purple-400 hover:bg-purple-500/20 hover:border-purple-400 transition-all"
              >
                🏆 View Achievements & Badges
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Grid by Category */}
      <div className="px-4 py-4">
        <div className="max-w-lg mx-auto space-y-6">
          {FEATURE_CATEGORIES.map((category) => (
            <div key={category.title}>
              {/* Category Header */}
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-lg font-semibold text-white">{category.title}</h2>
                {phase < category.phase && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-500">
                    Phase {category.phase}
                  </span>
                )}
              </div>

              {/* Feature Cards Grid */}
              <div className="grid grid-cols-2 gap-3">
                {category.features.map((featureId) => {
                  const feature = UNLOCKABLE_FEATURES[featureId];
                  if (!feature) return null;
                  
                  const unlocked = isFeatureUnlocked(featureId);
                  const Icon = feature.icon;

                  return (
                    <button
                      key={featureId}
                      onClick={() => handleFeatureClick(featureId)}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        unlocked
                          ? 'bg-gray-800/50 border-gray-700 hover:bg-gray-800 hover:border-gray-600'
                          : 'bg-gray-900/30 border-gray-800/50'
                      }`}
                      data-testid={`feature-card-${featureId}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          unlocked ? 'bg-emerald-500/20' : 'bg-gray-800'
                        }`}>
                          {unlocked ? (
                            <Icon className="w-5 h-5 text-emerald-400" />
                          ) : (
                            <Lock className="w-5 h-5 text-gray-500" />
                          )}
                        </div>
                        {!unlocked && (
                          <div className="flex items-center gap-1 text-amber-400/70">
                            <Coins className="w-3 h-3" />
                            <span className="text-xs font-medium">{feature.coinCost}</span>
                          </div>
                        )}
                      </div>
                      <div className={`font-medium text-sm ${unlocked ? 'text-white' : 'text-gray-500'}`}>
                        {feature.name}
                      </div>
                      <div className={`text-xs mt-1 line-clamp-2 ${unlocked ? 'text-gray-400' : 'text-gray-600'}`}>
                        {feature.description.split('.')[0]}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Your Golf Journey - Condensed */}
      <div className="px-4 py-4">
        <div className="max-w-lg mx-auto">
          <h2 className="text-lg font-semibold text-white mb-3">Your Journey</h2>
          
          <div className="flex gap-2 overflow-x-auto pb-2">
            {Object.entries(PHASES).map(([phaseNum, config]) => {
              const num = parseInt(phaseNum);
              const isCurrentPhase = phase === num;
              const isCompleted = phase > num;
              const Icon = config.icon;

              return (
                <div
                  key={phaseNum}
                  className={`flex-shrink-0 px-4 py-3 rounded-xl border transition-all ${
                    isCurrentPhase
                      ? `${config.bgClass} ${config.borderClass}`
                      : isCompleted
                      ? 'bg-gray-800/30 border-gray-700/50'
                      : 'bg-gray-900/30 border-gray-800/30 opacity-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isCompleted ? 'bg-emerald-500/20' : isCurrentPhase ? config.badgeBg : 'bg-gray-800'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Icon className={`w-4 h-4 ${isCurrentPhase ? config.textClass : 'text-gray-500'}`} />
                      )}
                    </div>
                    <span className={`font-medium text-sm ${
                      isCurrentPhase ? 'text-white' : isCompleted ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {config.name}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      {recentScores.length > 0 && (
        <div className="px-4 py-4">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-white">Recent Rounds</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/history')}
                className="text-gray-400 hover:text-white"
              >
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            <div className="space-y-2">
              {recentScores.slice(0, 3).map((score) => (
                <div
                  key={score.score_id}
                  className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-gray-700/50"
                >
                  <div>
                    <div className="font-medium text-white text-sm">
                      {score.course_name || "Unknown Course"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {score.date ? new Date(score.date).toLocaleDateString() : ""}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-white">{score.total_score}</div>
                    <div className={`text-xs ${
                      score.total_score - (score.par || 72) > 0 
                        ? 'text-red-400' 
                        : score.total_score - (score.par || 72) < 0 
                        ? 'text-emerald-400' 
                        : 'text-gray-400'
                    }`}>
                      {score.total_score - (score.par || 72) > 0 ? '+' : ''}
                      {score.total_score - (score.par || 72)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewUserDashboard;
